import { Pagination, Table } from "antd";
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { FilterList, FormFieldType, ContentLoader, ContentNoData } from "@components";
import { history, routerPath } from "@config";
import { ApplicationsActions } from "@redux";
import {
  checkJsonProp,
  dateFormat,
  formatDate,
  i18,
  translationGroups,
  trb,
  writeExcelFile
} from "@utilities";

const APP_STATUS = ["Pending", "Complete", "Rejected"];
const INITIAL_ROWS_PER_PAGE = 20;
const INITIAL_TABLE_FILTERS = {
  status: "Pending",
  applicationProcessType: "",
  needActionFromEmployee: true,
  clientName: "",
  rowsPerPage: INITIAL_ROWS_PER_PAGE,
  page: 1
};
const EXCEL_COL_WIDTH = [
  { wch: 40 },
  { wch: 40 },
  { wch: 25 },
  { wch: 40 },
  { wch: 10 },
  { wch: 35 },
  { wch: 40 },
  { wch: 30 },
  { wch: 15 },
  { wch: 15 },
  { wch: 15 }
];

export const ApplicationTable = React.memo(() => {
  const dispatch = useDispatch();
  const { applicationsList, sendApplications: isLoading } = useSelector(
    s => s.ApplicationsReducers
  );

  const [tableFilters, setTableFilters] = useState(INITIAL_TABLE_FILTERS);
  const [isExcelDownloading, setIsExcelDownloading] = useState(false);
  const mountedOnce = useRef(false);

  useEffect(() => {
    (async () => {
      if (!applicationsList && !mountedOnce.current) {
        await dispatch(ApplicationsActions.getApplicationList(INITIAL_TABLE_FILTERS));
        mountedOnce.current = true;
      }
    })();
  }, []);

  const filterListInputsMemo = useMemo(
    () => [
      {
        name: "clientName",
        component: FormFieldType.input,
        title: i18.ApplicationsLabels.ClientName,
        translate: translationGroups.SanctionStatus,
        value: tableFilters.clientName
      },
      {
        name: "status",
        component: FormFieldType.select,
        allowClear: true,
        title: i18.ApplicationsLabels.Status,
        options: APP_STATUS.map(item => {
          return { value: item, label: item };
        }),
        optionsValue: "value",
        translate: translationGroups.ApplicationStatus,
        value: tableFilters.status
      },
      {
        name: "applicationProcessType",
        component: FormFieldType.select,
        allowClear: true,
        title: i18.ApplicationsLabels.ProcessType,
        options: [{ value: "PeriodicReview", label: trb(i18.Labels.PeriodicReview) }, {value: "Onboarding", label: trb(i18.Labels.Onboarding)}],
        optionsValue: "value",
        translate: translationGroups.ApplicationProcessType,
        value: tableFilters.applicationProcessType
      },
      {
        name: "needActionFromEmployee",
        component: FormFieldType.select,
        allowClear: true,
        title: i18.ApplicationsLabels.NeedActionFromEmployee,
        options: [
          {
            label: "True",
            value: true
          },
          {
            label: "False",
            value: false
          }
        ],
        optionsLabel: "label",
        optionsValue: "value",
        translate: translationGroups.Labels,
        value: tableFilters.needActionFromEmployee
      }
    ],
    [tableFilters]
  );

  const columns = [
    {
      title: trb(i18.ApplicationsLabels.ApplicationNumber),
      dataIndex: "number",
      key: "number"
    },
    {
      title: trb(i18.ApplicationsLabels.ClientCode),
      dataIndex: "clientCode",
      key: "clientCode"
    },
    {
      title: trb(i18.ApplicationsLabels.ClientName),
      dataIndex: "clientName",
      key: "clientName"
    },
    {
      title: trb(i18.ApplicationsLabels.ProcessType),
      dataIndex: "isRenewKyc",
      key: "isRenewKyc",
      render: item => (item ? trb(i18.Labels.PeriodicReview) : trb(i18.Labels.Onboarding))
    },
    {
      title: trb(i18.ApplicationsLabels.State),
      dataIndex: "state",
      key: "state",
      render: item => trb(translationGroups.ApplicationState + item)
    },
    {
      title: trb(i18.ApplicationsLabels.Status),
      dataIndex: "status",
      key: "status",
      render: item => trb(translationGroups.ApplicationStatus + item)
    },
    {
      title: trb(i18.ApplicationsLabels.Created),
      dataIndex: "created",
      key: "created",
      render: item => formatDate(item, dateFormat().dateTime)
    },
    {
      title: trb(i18.ApplicationsLabels.NeedUserAction),
      dataIndex: "needUserAction",
      key: "needUserAction",
      render: item => (item ? trb(i18.Labels.True) : trb(i18.Labels.False))
    },
    {
      title: trb(i18.ApplicationsLabels.NeedsEmployeeAction),
      dataIndex: "needsEmployeeAction",
      key: "needsEmployeeAction",
      render: item => (item ? trb(i18.Labels.True) : trb(i18.Labels.False))
    }
  ];

  const onFilter = async filterValues => {
    const newTableFilterState = {
      ...filterValues,
      page: 1,
      rowsPerPage: 1000
    };
    setTableFilters(newTableFilterState);
    await dispatch(ApplicationsActions.getApplicationList(newTableFilterState));
  };

  const handlePageChange = async (pageNumber, pageSize) => {
    const newTableFilterState = {
      ...tableFilters,
      page: pageNumber,
      rowsPerPage: pageSize
    };
    setTableFilters(newTableFilterState);
    await dispatch(ApplicationsActions.getApplicationList(newTableFilterState));
  };

  const handleListDownload = async () => {
    setIsExcelDownloading(true);
    try {
      const { items } = await dispatch(
        ApplicationsActions.getApplicationListForExcel({
          ...tableFilters,
          rowsPerPage: applicationsList.paging.totalItems
        })
      );
      writeExcelFile({
        items,
        fileName: "Applications",
        colWidths: EXCEL_COL_WIDTH
      });
      setIsExcelDownloading(false);
    } catch {
      setIsExcelDownloading(false);
    }
  };

  if (isLoading && !applicationsList) return <ContentLoader />;
  if (!isLoading && !applicationsList && mountedOnce.current) return <ContentNoData />;

  return (
    <div>
      <div className="kycApplicationFilter">
        <FilterList
          inputs={filterListInputsMemo}
          callback={onFilter}
          onDownload={handleListDownload}
          isExcelDownloading={isExcelDownloading}
        />
      </div>
      <Table
        className="w-100 mb-3"
        dataSource={applicationsList?.items}
        columns={columns}
        scroll={{ x: "60vw" }}
        pagination={false}
        rowKey="id"
        onRow={record => {
          return {
            onClick: () => history.push(`${routerPath.listKyc}/${record.id}`)
          };
        }}
      />
      <div className="text-right">
        <Pagination
          showSizeChanger
          pageSize={tableFilters.rowsPerPage}
          current={tableFilters.page}
          onChange={handlePageChange}
          defaultCurrent={1}
          total={checkJsonProp(applicationsList, "paging.totalItems")}
        />
      </div>
    </div>
  );
});
